import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/AuthContext';
import { usePeriod } from '@/app/PeriodSwitcher';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { FileUpload } from '@/components/ui/FileUpload';
import { assessmentApi } from '@/features/assessments/api';
import { databaseApi, formatDuration, formatMonthYear, type TbmDetail } from './api';

const TABS = ['Profil', 'PELITA SLI', 'Penilaian', 'Monev', 'Refleksi', 'Berkas'] as const;
type Tab = (typeof TABS)[number];

/** L-05b — Detail TBM. Tab kosong sengaja sudah ada agar navigasi tidak dibongkar ulang. */
export function TbmDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('Profil');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tbm', id],
    queryFn: () => databaseApi.getTbm(id),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-5">
      <Link to="/database/tbm" className="text-sm text-ink-secondary hover:text-primary">
        ← Database TBM
      </Link>

      <TbmHeader
        tbm={data}
        canEdit={user?.role === 'super_admin'}
        onEdit={() => navigate(`/database/tbm/${id}/ubah`)}
      />

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'min-h-11 shrink-0 border-b-2 px-4 text-[15px] font-medium',
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-secondary hover:text-ink',
            )}
          >
            {t}
            {t === 'PELITA SLI' && ` (${data.members.length})`}
          </button>
        ))}
      </div>

      {tab === 'Profil' && <ProfileTab tbm={data} />}
      {tab === 'PELITA SLI' && <MembersTab tbm={data} />}
      {tab === 'Penilaian' && <TbmAssessmentsTab tbmId={id} />}
      {tab === 'Monev' && <TbmMonevTab tbm={data} />}
      {tab === 'Refleksi' && <TbmReflectionsTab tbmId={id} />}
      {tab === 'Berkas' && <TbmFilesTab tbmId={id} photoKey={data.photoKey} />}
    </div>
  );
}

function TbmHeader({
  tbm,
  canEdit,
  onEdit,
}: {
  tbm: TbmDetail;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { current } = usePeriod();
  const duration = formatDuration(tbm.joinedPeriod);

  return (
    <Card>
      <div className="flex flex-wrap items-start gap-5">
        {tbm.photoKey ? (
          <img
            src={tbm.photoKey.startsWith('http') ? tbm.photoKey : `/api/v1/files/${tbm.photoKey}`}
            alt={tbm.name}
            className="size-20 shrink-0 rounded-[var(--radius-input)] object-cover border border-border shadow-sm"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              if (e.currentTarget.nextElementSibling) {
                (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'grid';
              }
            }}
          />
        ) : null}
        <div
          className={cn(
            'grid size-20 shrink-0 place-items-center rounded-[var(--radius-input)] bg-primary-soft text-3xl',
            tbm.photoKey && 'hidden',
          )}
        >
          📚
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-2xl font-semibold text-ink">{tbm.name}</h1>
            <StatusBadge status={tbm.status} />
            {tbm.angkatan && (
              <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-[12px] font-semibold text-primary">
                {tbm.angkatan}
              </span>
            )}
          </div>

          <p className="mt-1 text-ink-secondary">
            <span className="tabular font-medium">{tbm.code}</span>
            {tbm.regionPath.length > 0 && ' · ' + tbm.regionPath.map((r) => r.name).join(', ')}
          </p>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
            {tbm.joinedPeriod && (
              <span>
                Bergabung sejak <strong>{formatMonthYear(tbm.joinedPeriod)}</strong>
                {duration && <span className="text-ink-secondary"> ({duration})</span>}
              </span>
            )}
            {current && (
              <span className="text-ink-secondary" title={current.description ?? undefined}>
                Periode aktif: <strong>{current.name}</strong>
                {current.description && (
                  <span className="ml-1 text-ink-muted">({current.description})</span>
                )}
              </span>
            )}
            {tbm.socialMedia && <span className="text-ink-secondary">{tbm.socialMedia}</span>}
          </div>
        </div>

        {canEdit && (
          <Button variant="secondary" onClick={onEdit}>
            Ubah
          </Button>
        )}
      </div>
    </Card>
  );
}

function ProfileTab({ tbm }: { tbm: TbmDetail }) {
  const duration = formatDuration(tbm.joinedPeriod);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Identitas</h2>
        <Rows
          items={[
            ['Kode', tbm.code],
            ['Nama', tbm.name],
            ['Angkatan', tbm.angkatan],
            ['Media sosial', tbm.socialMedia],
          ]}
        />
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Lokasi</h2>
        <Rows
          items={tbm.regionPath.map((r) => [LEVEL_LABEL[r.level], r.name] as [string, string])}
        />
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Keikutsertaan Program</h2>
        <Rows
          items={[
            ['Angkatan', tbm.angkatan],
            ['Bulan bergabung', formatMonthYear(tbm.joinedPeriod)],
            ['Lama bergabung', duration || '—'],
            ['Status keikutsertaan', tbm.status === 'active' ? 'Aktif' : tbm.status],
            [
              'Assessor pendamping',
              tbm.assessors.length ? tbm.assessors.map((a) => a.name).join(', ') : null,
            ],
          ]}
        />
      </Card>
    </div>
  );
}

function MembersTab({ tbm }: { tbm: TbmDetail }) {
  if (tbm.members.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="👥"
          title="Belum ada PELITA SLI terdaftar"
          description="Tambahkan individu PELITA SLI dari menu Database › PELITA SLI, lalu tautkan ke TBM ini."
        />
      </Card>
    );
  }

  return (
    <Card padded={false}>
      <ul className="divide-y divide-[color:var(--color-border)]">
        {tbm.members.map((m) => (
          <li key={m.individualId}>
            <Link
              to={`/database/pelita/${m.individualId}`}
              className="flex items-center gap-3.5 px-5 py-4 hover:bg-app"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
                {m.name
                  .replace(/^\[Contoh\]\s*/, '')
                  .split(' ')
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join('')}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{m.name}</p>
                <p className="tabular text-[13px] text-ink-secondary">{m.code}</p>
              </div>
              <StatusBadge status={m.status} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function TbmAssessmentsTab({ tbmId }: { tbmId: string }) {
  const navigate = useNavigate();
  const { data: rows, isLoading, error, refetch } = useQuery({
    queryKey: ['assessments', 'tbm', tbmId],
    queryFn: () => assessmentApi.list({ tbmId }),
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!rows || rows.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="📝"
          title="Belum ada riwayat penilaian"
          description="Penilaian untuk TBM ini akan muncul di sini setelah dibuat dari menu Pengukuran."
        />
      </Card>
    );
  }

  return (
    <Card padded={false} className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-border text-[13px] font-medium text-ink-secondary">
            <th className="px-4 py-3">Periode</th>
            <th className="px-4 py-3">Instrumen</th>
            <th className="px-4 py-3">Angkatan</th>
            <th className="px-4 py-3">Indeks</th>
            <th className="px-4 py-3">Kategori</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border text-sm last:border-0 hover:bg-app/50">
              <td className="px-4 py-3 font-medium text-ink">{row.periodName}</td>
              <td className="px-4 py-3 text-ink-secondary">{row.instrumentName}</td>
              <td className="px-4 py-3 tabular">{row.angkatan ?? '—'}</td>
              <td className="px-4 py-3 tabular font-semibold text-ink">
                {row.totalScore === null ? '—' : Math.round(row.totalScore * 100) / 100}
              </td>
              <td className="px-4 py-3">{row.band ?? <span className="text-ink-muted">—</span>}</td>
              <td className="px-4 py-3">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="secondary" onClick={() => navigate(`/pengukuran/${row.id}`)}>
                  Buka Penilaian
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

interface MonevFinding {
  id: string;
  code: string;
  kind: 'nonconformity' | 'success';
  regionName: string;
  periodName: string;
  foundDate: string | null;
  weakIndicator: string | null;
  description: string;
  dueDate: string | null;
  status: 'open' | 'in_progress' | 'resolved';
}

function TbmMonevTab({ tbm }: { tbm: TbmDetail }) {
  const cityRegion = tbm.regionPath.find((r) => r.level === 'city');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['monev-findings', cityRegion?.id],
    queryFn: () =>
      cityRegion
        ? api.get<MonevFinding[]>(`/monev/findings?regionId=${cityRegion.id}`)
        : Promise.resolve([]),
    enabled: Boolean(cityRegion),
  });

  if (!cityRegion) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="📍"
          title="Kabupaten belum terdefinisi"
          description="Wilayah kabupaten TBM ini belum lengkap, sehingga risalah monev wilayah belum dapat dimuat."
        />
      </Card>
    );
  }

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data || data.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="🔍"
          title="Belum ada risalah monev"
          description={`Belum ada temuan monev tercatat untuk wilayah ${cityRegion.name}.`}
        />
      </Card>
    );
  }

  return (
    <Card padded={false} className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-border text-[13px] font-medium text-ink-secondary">
            <th className="px-4 py-3">Kode</th>
            <th className="px-4 py-3">Jenis</th>
            <th className="px-4 py-3">Uraian Risalah</th>
            <th className="px-4 py-3">Periode</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {data.map((f) => (
            <tr key={f.id} className="border-b border-border text-sm last:border-0 hover:bg-app/50">
              <td className="px-4 py-3 tabular font-medium text-ink">{f.code}</td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-semibold',
                    f.kind === 'nonconformity'
                      ? 'bg-danger-soft text-danger'
                      : 'bg-success-soft text-success',
                  )}
                >
                  {f.kind === 'nonconformity' ? 'Ketidaksesuaian' : 'Keberhasilan'}
                </span>
              </td>
              <td className="px-4 py-3 text-ink max-w-md">
                <p className="line-clamp-2">{f.description}</p>
                {f.weakIndicator && (
                  <p className="mt-0.5 text-xs text-ink-muted">Indikator: {f.weakIndicator}</p>
                )}
              </td>
              <td className="px-4 py-3 text-ink-secondary text-xs">{f.periodName}</td>
              <td className="px-4 py-3">
                <StatusBadge status={f.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

interface Reflection {
  id: string;
  programName: string;
  observedDate: string | null;
  status: 'draft' | 'final';
  updatedAt: string;
}

function TbmReflectionsTab({ tbmId }: { tbmId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reflections', tbmId],
    queryFn: () => api.get<Reflection[]>(`/monev/reflections?tbmId=${tbmId}`),
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data || data.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="💡"
          title="Belum ada lembar refleksi"
          description="Refleksi program per periode untuk TBM ini akan tampil di sini setelah dibuat pada menu Monev & Refleksi."
        />
      </Card>
    );
  }

  return (
    <Card padded={false} className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-border text-[13px] font-medium text-ink-secondary">
            <th className="px-4 py-3">Nama Program</th>
            <th className="px-4 py-3">Tanggal Pelaksanaan</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Terakhir Diperbarui</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.id} className="border-b border-border text-sm last:border-0 hover:bg-app/50">
              <td className="px-4 py-3 font-medium text-ink">{r.programName}</td>
              <td className="px-4 py-3 text-ink-secondary tabular">{r.observedDate ?? '—'}</td>
              <td className="px-4 py-3">
                <StatusBadge status={r.status} />
              </td>
              <td className="px-4 py-3 text-ink-muted text-xs tabular">
                {new Date(r.updatedAt).toLocaleDateString('id-ID')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function TbmFilesTab({ tbmId, photoKey }: { tbmId: string; photoKey: string | null }) {
  return (
    <div className="flex flex-col gap-4">
      {photoKey && (
        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Foto / Logo TBM</h2>
          <div className="flex items-center gap-4">
            <img
              src={photoKey.startsWith('http') ? photoKey : `/api/v1/files/${photoKey}`}
              alt="Logo TBM"
              className="size-20 rounded-[var(--radius-input)] object-cover border border-border shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <p className="text-sm text-ink-secondary">
              Logo resmi TBM yang tersimpan di sistem. Untuk mengganti logo, gunakan tombol Ubah di bagian atas.
            </p>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="mb-2 text-[17px] font-semibold">Dokumen & Berkas Pendukung</h2>
        <p className="mb-4 text-sm text-ink-secondary">
          Unggah berkas legalitas atau kegiatan TBM seperti SK Pendirian, MoU, Sertifikat, atau Foto Kegiatan (PDF, JPG, PNG, WebP maks 5 MB).
        </p>
        <FileUpload
          entityType="tbm_document"
          entityId={tbmId}
          label="Daftar Dokumen TBM"
        />
      </Card>
    </div>
  );
}

const LEVEL_LABEL: Record<string, string> = {
  province: 'Provinsi',
  city: 'Kabupaten',
  village: 'Desa/Nagari',
};

function Rows({ items }: { items: [string, string | number | null | undefined][] }) {
  return (
    <dl className="flex flex-col gap-3">
      {items.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[140px_1fr] gap-3 text-[15px]">
          <dt className="text-ink-secondary">{label}</dt>
          <dd className={value ? 'text-ink' : 'text-ink-muted'}>{value || 'Belum diisi'}</dd>
        </div>
      ))}
    </dl>
  );
}
